/**
 * 统一日志工具
 * 开发环境输出日志，生产环境禁用 debug/info 级别
 */
/* eslint-disable no-console */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment: boolean;

  constructor() {
    // 判断是否为开发环境
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * 格式化日志消息
   */
  private formatMessage(
    level: LogLevel,
    message: string,
    ...args: unknown[]
  ): unknown[] {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    return [prefix, message, ...args];
  }

  /**
   * Debug 级别日志（仅开发环境）
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.log(...this.formatMessage('debug', message, ...args));
    }
  }

  /**
   * Info 级别日志（仅开发环境）
   */
  info(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.info(...this.formatMessage('info', message, ...args));
    }
  }

  /**
   * Warning 级别日志（所有环境）
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(...this.formatMessage('warn', message, ...args));
  }

  /**
   * Error 级别日志（所有环境）
   */
  error(message: string, ...args: unknown[]): void {
    console.error(...this.formatMessage('error', message, ...args));
  }

  /**
   * 创建带前缀的 logger（用于模块级别的日志）
   * @example
   * const logger = createLogger('WorkHourModal');
   * logger.debug('handleDateChange called', { dates });
   */
  createScoped(scope: string): ScopedLogger {
    return new ScopedLogger(scope, this.isDevelopment);
  }
}

/**
 * 带作用域的 Logger
 */
class ScopedLogger {
  constructor(
    private scope: string,
    private isDevelopment: boolean
  ) {}

  private formatMessage(
    level: LogLevel,
    message: string,
    ...args: unknown[]
  ): unknown[] {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}] [${this.scope}]`;
    return [prefix, message, ...args];
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.log(...this.formatMessage('debug', message, ...args));
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.info(...this.formatMessage('info', message, ...args));
    }
  }

  warn(message: string, ...args: unknown[]): void {
    console.warn(...this.formatMessage('warn', message, ...args));
  }

  error(message: string, ...args: unknown[]): void {
    console.error(...this.formatMessage('error', message, ...args));
  }
}

// 导出单例
export const logger = new Logger();

// 导出创建作用域 logger 的快捷方式
export const createLogger = (scope: string) => logger.createScoped(scope);

// 默认导出
export default logger;
